import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert
} from "@mui/material";

import {
  Send,
  Check,
  Cancel,
  CheckCircle,
  HourglassEmpty
} from '@mui/icons-material';

import { LoadingButton } from '@mui/lab';


import BackDrop from '../../../shared/backdropDE';
import { ACTIONS, MESSAGES } from '../../../shared/constants';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AlertDialogQuestion from '../../alert-dialog-question';
import { getUser } from '../../../shared/auth-service';
import dayjs from 'dayjs';


const AbsencePermissionsDetail = ({
  handleClose,
  handleSave,
  handleEdit,
  data,
  saving
}) => {
  const [loading, setLoading] = useState(false);

  // fields
  const [employeeAbsencePermissionsId, setEmployeeAbsencePermissionsId] = useState(data.employeeAbsencePermissionsId);
  const [requirementType, setRequirementType] = useState(data.requirementType);
  const [employeeId, setEmployeeId] = useState(data.employeeId);
  const [airportId, setAirportId] = useState(data.airportId);
  const [absenceType, setAbsenceType] = useState(data.absenceType);
  const [absenceDescription, setAbsenceDescription] = useState(data.absenceDescription);
  const [isDisabled, setIsDisabled] = useState(true);
  const [employeeArea, setEmployeeArea] = useState(data.employeeArea);
  const [startDate, setStartDate] = useState(data.startDate ? dayjs(data.startDate) : null);
  const [endDate, setEndDate] = useState(data.endDate ? dayjs(data.endDate) : null);  
  const [justify, setJustify] = useState(data.justify);
  const [isResponse, setIsResponse] = useState((data.requirementType > 0 && data.isActive) ? true : false);
  const [response, setResponse] = useState(data.response);
  const [isVerified, setIsVerified] = useState(data.isVerified);
  const [isActive, setIsActive] = useState(data.isActive);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isActiveCheck, setIsActiveCheck] = useState((data.requirementType > 0 && data.isActive) ? true : false);
  const [alertQuestionOpen, setAlertQuestionOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isNew, setIsNew] = useState(data.isNew);
  const [isRead, setIsRead] = useState(data.isRead);
  const user = getUser();
  const airport = user.airport.code;
  const employeeIdSession = user.employeeId;

  // --- REGLAS DE FECHAS ---
  const TYPE = { AUSENCIA: 1, PERMISO: 2, CUMPLE: 3, FAMILIA: 4 };
  const asNum = (v) => Number(v ?? 0);


  const anchor = useMemo(() => dayjs().startOf('day'), []);
  //const anchor  = useMemo(() => dayjs('2025-09-20').startOf('day'),   []);

  const isFirstTen = anchor.date() <= 10;

  const nextMonthStart = useMemo(() => anchor.add(1,'month').startOf('month'), [anchor]);
  const nextMonthEnd   = useMemo(() => anchor.add(1,'month').endOf('month'),   [anchor]);

  const isRestrictedType = (t) =>
    [TYPE.PERMISO, TYPE.CUMPLE, TYPE.FAMILIA].includes(asNum(t));

  const isSingleDayType =
    [TYPE.CUMPLE, TYPE.FAMILIA].includes(asNum(requirementType));

  const clampToNextMonth = (d) => {
    if (!d) return d;
    if (d.isBefore(nextMonthStart, 'day')) return nextMonthStart;
    if (d.isAfter(nextMonthEnd, 'day'))   return nextMonthEnd;
    return d;
  };

  const limits = useMemo(() => {
    if (!isNew || isRead) 
      return { min: undefined, max: undefined };

    if (isRestrictedType(requirementType)) 
      return { min: nextMonthStart, max: nextMonthEnd };
    
    return { min: anchor.subtract(15,'day'), max: undefined };
  }, [isNew, isRead, requirementType, anchor, nextMonthStart, nextMonthEnd]);

  const restrictedNames = airport === "BOG" ? ["permisos", "día de la familia"]: ["permisos", "cumpleaños", "día de la familia"];

  const humanJoin = (arr) => arr.length === 2 ? `${arr[0]} y ${arr[1]}` : `${arr.slice(0, -1).join(", ")} y ${arr.slice(-1)}`;

  
  // variables
  const absence = [
    {type: 1, description: "Incapacidad"},
    {type: 2, description: "Licencia"},
    {type: 3, description: "Calamidad"},
    {type: 4, description: "Otro"},
  ];

  const validateForm = () => {
    const t = asNum(requirementType);

    if (isSingleDayType) {
      if (!startDate || !endDate) 
        return true;
      if (!startDate.isSame(endDate, 'day')) 
        return true;
    }


    if (startDate && endDate && startDate.isAfter(endDate)) 
      return true;

    if ([TYPE.CUMPLE, TYPE.FAMILIA].includes(t) && startDate && endDate && !startDate.isSame(endDate, 'day')) 
      return true;

    // 2/3/4 → Solo primeros 10 días + fechas dentro del mes siguiente
    if (isRestrictedType(t)) {
      if (!isFirstTen) 
        return true;
      if (!startDate || !endDate) 
        return true;
      const outOfRange = startDate.isBefore(nextMonthStart, 'day') || startDate.isAfter(nextMonthEnd, 'day') || endDate.isBefore(nextMonthStart, 'day')   || endDate.isAfter(nextMonthEnd, 'day');
      if (outOfRange) 
        return true;
    }

    return (
      !t || t === 0 ||!startDate || !endDate || !justify.trim() ||
      (t === TYPE.AUSENCIA &&
        (!absenceType || absenceType === 0 || (absenceType === 4 && !absenceDescription.trim())))
    );
  };



  const handleMarkChange = (event) => {
    setIsDeleted(event.target.checked);
    setIsActive(!event.target.checked);
  };

  const handleAbsenceType = (e) => {
    setAbsenceType(e);
    setIsDisabled((e == 4) ? false : true);
    setAbsenceDescription((e == 4) ? absenceDescription : "");
  };

  const handleRequerimentMessage = (e) => {
    const t = asNum(e);

    if (isRestrictedType(t) && !isFirstTen) {
      setAlertQuestionOpen(true);
      setMessage(
        <>¡Planea con tiempo! Podrás solicitar tus <strong>{humanJoin(restrictedNames)}</strong> solo durante los <strong>primeros 10 días de cada mes.</strong> <br></br>
        Estas solicitudes aplicarán para el <strong>mes siguiente</strong>, previa aprobación del área encargada de tu base.</>
      );
      return;
    }


    // Cambia el tipo y LIMPIA fechas
    setRequirementType(t);
    setStartDate(null);
    setEndDate(null);

    setAlertQuestionOpen(true);

    if (t === TYPE.AUSENCIA) {
      const msg = airport !== "BOG"
        ? <><strong>Ten en cuenta que este reporte solo registra tu ausencia.</strong><br /><br />Aún debes presentar el soporte válido ante el área encargada de tu base para formalizarlo.</>
        : <><strong>Ten en cuenta que este reporte solo registra tu ausencia.</strong><br /><br />Aún debes presentar el soporte válido ante el Centro de Atención al Colaborador en P30.</>;
      setMessage(msg);
      return;
    }

    // 2/3/4 → Fechas del mes siguiente obligatorias
    let start = startDate ? clampToNextMonth(startDate) : nextMonthStart;
    let end   = endDate   ? clampToNextMonth(endDate)   : nextMonthStart;

    if (t === TYPE.CUMPLE || t === TYPE.FAMILIA) {
      end = start; // un solo día
    }

    setStartDate(start);
    setEndDate(end);

    setMessage(
      <ol style={{ paddingLeft: '1.2rem', lineHeight: 1.7 }}>
        <li>Realiza la solicitud en los <strong>10 primeros días del mes anterior</strong> a la fecha deseada. Las solicitudes recibidas fuera de este periodo no se tendrán en cuenta.</li>
        <li>Incluye claramente la fecha de inicio y fin del permiso.</li>
        <li>Las solicitudes se aprueban según prioridad, operación y otros criterios.</li>
        <li>No se autorizan permisos no remunerados.</li>
        <li>Presenta documentos soporte si aplica.</li>
        <li>Recibirás respuesta por los canales oficiales de Talma antes de la publicación de turnos.</li>
      </ol>
    );
  };



  const handlePositive = () => {
    //console.log("Acepta Condiciones");
    setAlertQuestionOpen(false);
  };
  
  const handleNegative = () => {
    handleClose(false);
    setAlertQuestionOpen(false);
  };

  return (
    <>
      {alertQuestionOpen && <AlertDialogQuestion
          handlePositive={handlePositive}
          handleNegative={handleNegative}
          textButtonPositive={ACTIONS.ACCEPT}
          textButtonNegative={ACTIONS.CANCEL}
          dialogContentText={message}
          dialogTitle={""}
          open={alertQuestionOpen}
          elemento={""}
          showCancelButton ={false}
      />}

      <Dialog
        maxWidth="sm"
        fullWidth={true}
        open={true}
        scroll="body"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Typography style={{ color: '#1A3072', fontWeight: 'bold', fontSize: '24px' }}>
            { (isNew) ? ("Nuevo"):("Detalle") } <span style={{ color: '#00B0EF' }}>Registro</span>
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ p: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="select-regtype">Tipo de Registro</InputLabel>
                <Select
                  label="Tipo de Registro"
                  labelId="select-regtype"
                  value={requirementType}
                  onChange={(e) => handleRequerimentMessage(e.target.value)}
                  disabled={isRead}
                >
                     <MenuItem value="0" disabled>Seleccionar una opción</MenuItem>
                      {/* Si es BOG mostramos Ausencia y ocultamos Cumpleaños */}
                      {airport === "BOG" && ([
                        <MenuItem key="1" value={1}>Ausencia</MenuItem>,
                        <MenuItem key="2" value={2}>Permiso</MenuItem>,
                        // <MenuItem key="4" value={4}>Día de la familia</MenuItem>,
                      ])}

                      {airport !== "BOG" && ([
                        <MenuItem key="1" value={1}>Ausencia</MenuItem>,
                        <MenuItem key="2" value={2}>Permiso</MenuItem>,
                        <MenuItem key="3" value={3}>Cumpleaños</MenuItem>,
                        // <MenuItem key="4" value={4}>Día de la familia</MenuItem>,
                      ])}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ p: 1 }}>
            { isActiveCheck && (
              <FormControl margin="dense">
                <FormControlLabel
                  control={
                    <Switch
                    checked={isDeleted} 
                    onChange={handleMarkChange}
                    />
                  }
                  label="¿Eliminar registro?"
                  labelPlacement="start"
                />
              </FormControl>
            )}
            </Grid>

            { requirementType == 1 && (
              <>
              <Grid item xs={12} md={6}  sx={{ p: 1 }}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="select-ausmot">Motivo de Ausencia</InputLabel>
                  <Select
                    label="motivo de ausencia"
                    labelId="select-ausmot"
                    value={absenceType}
                    onChange={(e) => handleAbsenceType(e.target.value)}
                    disabled={isRead}
                  >
                      <MenuItem value="0" disabled>Seleccionar una opción</MenuItem>
                    {absence.map((option) => (
                      <MenuItem key={option.type} value={option.type}>{option.description}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}  sx={{ p: 1 }}>
                <FormControl fullWidth={true} margin="dense">
                  <TextField
                    label="Otro motivo"
                    placeholder="¿Cúal?"
                    value={absenceDescription}
                    onChange={(e) => setAbsenceDescription(e.target.value)}
                    type='text'
                    disabled={isDisabled}
                  />
                </FormControl>
              </Grid>
              </>
            )}

            <Grid item xs={12} md={6} sx={{ p: 1 }}>
             <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={isRead}
                  label="Fecha Desde"
                  value={startDate}
                  onChange={(date) => {
                    if (date === null) { 
                      setStartDate(null); 
                      return; 
                    }
                    const d = isRestrictedType(requirementType) ? clampToNextMonth(date) : date;
                    setStartDate(d);

                    // si es Cumpleaños o Día de la Familia → forzar fecha hasta = fecha desde
                    if (isSingleDayType) 
                      setEndDate(d);
                  }}
                  minDate={limits.min}
                  maxDate={limits.max}
                  renderInput={(params) => (<TextField {...params} fullWidth />)}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6} sx={{ p: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={isRead || isSingleDayType}
                label="Fecha Hasta"
                value={endDate}
                onChange={(date) => {
                  if (date === null) { 
                    setEndDate(null); 
                    return; 
                  }
                  const d = isRestrictedType(requirementType) ? clampToNextMonth(date) : date;
                  setEndDate(d);
                }}
                minDate={limits.min}
                maxDate={limits.max}
                renderInput={(params) => (<TextField {...params} fullWidth />)}
              />
            </LocalizationProvider>
            </Grid>

            {/* <Grid item xs={12} md={12} sx={{ px: 1 }}>
              <FormControl fullWidth={true}>
                  <TextField
                    label="Área"
                    value={employeeArea}
                    onChange={(e) => setEmployeeArea(e.target.value)}
                    type='text'
                    InputProps={{ disabled: isRead }}
                  />
                </FormControl>
            </Grid> */}

            <Grid item xs={12} md={12} sx={{ px: 1, pt: 2 }}>
              <FormControl fullWidth={true}>
                  <TextField
                    label="Justificación"
                    value={justify}
                    onChange={(e) => setJustify(e.target.value)}
                    multiline
                    minRows={1}
                    maxRows={3}
                    InputProps={{ disabled: isRead }}
                  />
                </FormControl>
            </Grid>

         {isRead && (
          <Grid item xs={12} sx={{ px: 1, pt: 2 }}>
            <Box
              sx={{
                backgroundColor: isVerified ? "#E6F4EA" : "#FFF8E1",
                border: `1px solid ${isVerified ? "#4CAF50" : "#FFB300"}`,
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', color: isVerified ? "#2E7D32" : "#FF6F00", display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {isVerified ? (
                  <>
                    <CheckCircle fontSize="small" color="success" /> Verificado
                  </>
                ) : (
                  <>
                    <HourglassEmpty fontSize="small" color="warning" /> En revisión
                  </>
                )}
              </Typography>

              {isVerified && response && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    Respuesta:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {response}
                  </Typography>
                </Box>
              )}
              </Box>
            </Grid>
          )}
          </Grid>
        </DialogContent>

      <DialogActions sx={{ m: 1 }}>
        <Box>
          {isNew ? (
         <LoadingButton
          color="primary"
          variant="contained"
          loading={saving}
          disabled={validateForm()}
          onClick={() => {
            handleSave({
              requirementType,
              employeeId,
              airportId,
              absenceType,
              absenceDescription,
              employeeArea,
              startDate,
              endDate,
              justify,
              response,
              isVerified,
              isActive
            });
          }}
          endIcon={<Send />}
        >
          {ACTIONS.SEND_ASSESMENT}
        </LoadingButton>
          ) : (
          <LoadingButton
            color="primary"
            variant="contained"
            loading={saving}
            disabled={isActive}
            onClick={() => {
              handleEdit({
                employeeAbsencePermissionsId,
                response,
                isVerified,
                isActive,
                ResponseEmployee: employeeIdSession
              });
            }}
            endIcon={<Check />}
          >
            {ACTIONS.SAVE}
          </LoadingButton>

          )}
        </Box>

        <Box sx={{ m: 1, position: 'relative' }}>
          <Button
            variant="outlined"
            onClick={() => { handleClose(false) }}
            endIcon={<Cancel />}
            autoFocus
          >
            {ACTIONS.CANCEL}
          </Button>
        </Box>
      </DialogActions>

        {loading && (<BackDrop />)}
      </Dialog>
    </>
  );
}

export default AbsencePermissionsDetail;
